import { useEffect } from 'react';

import {
  isRouteErrorResponse,
  type NavigateFunction,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { getNonEmptyString, pickObjectString } from '@/lib/error-object';
import { captureRouteErrorTelemetry } from '@/telemetry/errorSink';

type RouteErrorKind = 'chunk' | 'auth' | 'not_found' | 'generic';

interface NormalizedRouteError {
  kind: RouteErrorKind;
  message: string;
  status: number | null;
  requestId: string | null;
  stack: string | null;
}

const CHUNK_ERROR_MARKERS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'unable to preload css for',
  'loading css chunk',
  'loading chunk',
  'chunkloaderror',
  'optimize deps directory',
  'dep optimizer',
  'the file does not exist at',
];

function pickMessageFromData(data: unknown): string | null {
  return getNonEmptyString(data) || pickObjectString(data, ['message', 'detail', 'error', 'title']);
}

function pickRequestId(data: unknown): string | null {
  return pickObjectString(data, ['request_id', 'requestId']);
}

function isLikelyChunkLoadError(message: string): boolean {
  const normalized = message.toLowerCase();
  return CHUNK_ERROR_MARKERS.some((marker) => normalized.includes(marker));
}

function resolveRouteErrorKind(message: string, status: number | null = null): RouteErrorKind {
  if (status === 401 || status === 403) {
    return 'auth';
  }
  if (status === 404) {
    return 'not_found';
  }
  return isLikelyChunkLoadError(message) ? 'chunk' : 'generic';
}

function normalizeRouteError(error: unknown): NormalizedRouteError {
  if (isRouteErrorResponse(error)) {
    const message = pickMessageFromData(error.data) || error.statusText || `HTTP ${error.status}`;
    const requestId = pickRequestId(error.data);

    return {
      kind: resolveRouteErrorKind(message, error.status),
      message,
      status: error.status,
      requestId,
      stack: null,
    };
  }

  if (error instanceof Error) {
    const message = error.message || 'Unexpected route error';
    const statusCandidate = (error as { status?: unknown }).status;
    const statusFromError =
      typeof statusCandidate === 'number' && Number.isFinite(statusCandidate)
        ? statusCandidate
        : null;

    return {
      kind: resolveRouteErrorKind(message, statusFromError),
      message,
      status: Number.isFinite(statusFromError) ? statusFromError : null,
      requestId: pickRequestId(error),
      stack: error.stack ?? null,
    };
  }

  const stringMessage = getNonEmptyString(error) || 'Unexpected route error';

  return {
    kind: resolveRouteErrorKind(stringMessage),
    message: stringMessage,
    status: null,
    requestId: pickRequestId(error),
    stack: null,
  };
}

function resolvePresentation(kind: RouteErrorKind): {
  title: string;
  description: string;
  primaryLabel: string;
  primaryAction: 'reload' | 'login';
} {
  switch (kind) {
    case 'chunk':
      return {
        title: 'Не удалось загрузить свежую версию приложения',
        description:
          'Обычно это происходит после обновления или при сбое сети. Обновите страницу, чтобы продолжить работу.',
        primaryLabel: 'Обновить страницу',
        primaryAction: 'reload',
      };
    case 'auth':
      return {
        title: 'Доступ к странице ограничен',
        description:
          'Похоже, сессия истекла или у аккаунта нет нужных прав. Войдите заново и попробуйте ещё раз.',
        primaryLabel: 'Войти заново',
        primaryAction: 'login',
      };
    case 'not_found':
      return {
        title: 'Страница не найдена',
        description:
          'Возможно, ссылка устарела или документ уже недоступен. Вернитесь в рабочий раздел и продолжайте.',
        primaryLabel: 'К списку документов',
        primaryAction: 'reload',
      };
    default:
      return {
        title: 'Что-то пошло не так',
        description:
          'Ошибка уже зафиксирована. Попробуйте обновить страницу или перейдите на главный экран.',
        primaryLabel: 'Обновить страницу',
        primaryAction: 'reload',
      };
  }
}

function resolveDevChunkHint(kind: RouteErrorKind): string | null {
  if (kind !== 'chunk' || !import.meta.env.DEV) {
    return null;
  }
  return 'Для localhost это часто означает stale Vite optimized deps. Если обновление не помогло, перезапустите frontend dev server.';
}

function shouldCaptureRouteTelemetry(kind: RouteErrorKind): boolean {
  return kind !== 'chunk';
}

function buildDiagnostics(fullPath: string, normalizedError: NormalizedRouteError): string {
  return [
    `route=${fullPath}`,
    `status=${normalizedError.status ?? 'n/a'}`,
    `request_id=${normalizedError.requestId ?? 'n/a'}`,
    `message=${normalizedError.message}`,
    normalizedError.stack ? `stack=${normalizedError.stack}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

function buildRouteErrorViewModel(
  pathname: string,
  search: string,
  hash: string,
  normalizedError: NormalizedRouteError,
) {
  const fullPath = `${pathname}${search}${hash}`;

  return {
    fullPath,
    diagnostics: buildDiagnostics(fullPath, normalizedError),
    presentation: resolvePresentation(normalizedError.kind),
    devChunkHint: resolveDevChunkHint(normalizedError.kind),
    showDiagnostics: import.meta.env.DEV || import.meta.env.MODE === 'test',
  };
}

function runPrimaryRouteErrorAction(
  action: 'reload' | 'login',
  navigate: NavigateFunction,
  fullPath: string,
) {
  if (action === 'login') {
    navigate('/webadmin/app/login', { replace: true, state: { from: fullPath } });
    return;
  }
  window.location.reload();
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 3L2 20h20L12 3z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 9v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1" fill="currentColor" />
    </svg>
  );
}

export default function RouteErrorBoundaryPage() {
  const error = useRouteError();
  const location = useLocation();
  const navigate = useNavigate();

  const normalizedError = normalizeRouteError(error);
  const { fullPath, diagnostics, presentation, devChunkHint, showDiagnostics } =
    buildRouteErrorViewModel(location.pathname, location.search, location.hash, normalizedError);
  const handlePrimaryAction = () =>
    runPrimaryRouteErrorAction(presentation.primaryAction, navigate, fullPath);

  useEffect(() => {
    if (!shouldCaptureRouteTelemetry(normalizedError.kind)) {
      return;
    }
    captureRouteErrorTelemetry(error, {
      route: fullPath,
      component: 'router.errorElement',
    });
  }, [error, fullPath, normalizedError.kind]);

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-background px-5 py-8">
      <div
        className={cn(
          'pointer-events-none absolute inset-0',
          'bg-[radial-gradient(circle_at_15%_20%,hsl(var(--color-muted))_0%,transparent_40%),radial-gradient(circle_at_85%_80%,hsl(var(--color-border))_0%,transparent_35%)]',
        )}
      />

      <section
        role="alert"
        data-testid="route-error-screen"
        className={cn(
          'relative w-full max-w-2xl rounded-2xl border border-border bg-card/95 p-6 shadow-[0_24px_64px_-32px_rgba(15,23,42,0.35)]',
          'sm:p-8',
        )}
      >
        <div className="flex items-start gap-4">
          <div className="rounded-xl border border-border bg-muted/70 p-2 text-destructive">
            <WarningIcon className="size-5" />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              System Error
            </p>
            <h1 className="text-balance text-2xl font-semibold text-foreground">
              {presentation.title}
            </h1>
            <p className="text-pretty text-sm text-muted-foreground">{presentation.description}</p>
          </div>
        </div>

        {normalizedError.requestId ? (
          <div className="mt-5 rounded-lg border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            Код запроса:{' '}
            <span className="font-mono text-foreground" data-testid="route-error-request-id">
              {normalizedError.requestId}
            </span>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button onClick={handlePrimaryAction}>
            {presentation.primaryLabel}
          </Button>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Назад
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/webadmin/app/invoices', { replace: true })}
          >
            На главную
          </Button>
        </div>

        {devChunkHint ? (
          <div className="mt-5 rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
            {devChunkHint}
          </div>
        ) : null}

        {showDiagnostics ? (
          <details className="mt-6 rounded-lg border border-border bg-muted/40 p-3">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              Технические детали (dev/test)
            </summary>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-foreground">
              {diagnostics}
            </pre>
          </details>
        ) : null}
      </section>
    </div>
  );
}
